import { Injectable } from "@angular/core";
import { FacebookLoginResponse } from "@awesome-cordova-plugins/facebook/ngx";
import {
  LoadingController,
  NavController,
  Platform,
} from "@ionic/angular/standalone";
import { firstValueFrom } from "rxjs";
import { Events } from "../../api/events/events";
import { UserProvider } from "../../api/user/user.api";
import { AppConfig } from "../../app.config";
import { FAEventTag } from "../../const/fa-event-tag";
import { LocalStorageKey } from "../../local-storage-key";
import { FacebookLoginError } from "../../models/errors";
import { LocationIds } from "../../models/location";
import {
  SignUpData,
  SignUpDataOAuth,
  SignUpDataPhoneAuth,
} from "../../models/signup-data";
import { Me } from "../../models/user";
import { AlertProvider } from "../../services/alert/alert.service";
import { appleAuth } from "../../services/auth/apple-auth.service";
import { Auth } from "../../services/auth/auth.service";
import { FacebookProvider } from "../../services/facebook/facebook";
import { LineProvider } from "../../services/line/line.service";
import { NavParamsService } from "../../services/nav-params/nav-params.service";
import { TrackerService } from "../../services/tracker/tracker.service";
import { Util } from "../../services/util/util";
import { WebViewProvider } from "../../services/web-view/web-view.service";

export type LoginProvider =
  | "phone"
  | "phone_for_cast"
  | "line"
  | "facebook"
  | "email"
  | "apple"
  | "apple_for_cast"
  | "line_with_pato_passport"
  | "line_with_pato_passport_cordova";

@Injectable({
  providedIn: "root",
})
export class OnboardingService {
  private loading: HTMLIonLoadingElement;

  constructor(
    private auth: Auth,
    private navCtrl: NavController,
    private alertCtrl: AlertProvider,
    private lineProvider: LineProvider,
    private facebookProvider: FacebookProvider,
    private webViewProvider: WebViewProvider,
    private tracker: TrackerService,
    private events: Events,
    private navParams: NavParamsService,
    private userProvider: UserProvider,
    private loadingCtrl: LoadingController,
    private platform: Platform
  ) {}

  /**
   * 共通ログイン処理
   */
  async handleLogin(provider: LoginProvider): Promise<void> {
    try {
      switch (provider) {
        case "phone":
          await this.authPhoneNumber(null, null, false);
          this.trackLogin(FAEventTag.Click.PhoneNumberAuth);
          break;
        case "phone_for_cast":
          await this.authPhoneNumber(null, null, true);
          this.trackLogin(FAEventTag.Click.PhoneNumberAuth);
          break;
        case "line":
          await this.authLine();
          this.trackLogin(FAEventTag.Click.LineAuth);
          break;
        case "line_with_pato_passport":
          await this.authLineWithPatoPassport();
          this.trackLogin(FAEventTag.Click.LineAuthWithPatoPassport);
          break;
        case "line_with_pato_passport_cordova":
          await this.authLineWithPatoPassportCordova();
          this.trackLogin(FAEventTag.Click.LineAuthWithPatoPassport);
          break;
        case "facebook":
          await this.authFacebook();
          this.trackLogin(FAEventTag.Click.FacebookAuth);
          break;
        case "email":
          await this.authMail();
          this.trackLogin(FAEventTag.Click.MailAuth);
          break;
        case "apple":
          await this.authApple(false);
          break;
        case "apple_for_cast":
          await this.authApple(true);
          break;
        default:
          throw new Error(`Unsupported login provider: ${provider}`);
      }
    } catch (error) {
      console.error(`Login failed for provider: ${provider}`, error);
      this.alertCtrl.basicAlert(
        "エラー",
        `${this.getProviderName(provider)}ログインに失敗しました`
      );
    }
  }

  /**
   * 利用規約ページを表示
   */
  showTermsPage(): void {
    this.webViewProvider.showTerms();
  }

  /**
   * プライバシーポリシーページを表示
   */
  showPrivacyPage(): void {
    this.webViewProvider.showPrivacy();
  }

  /**
   * キャストのinreviewステータスに基づいてページナビゲーションを行う（公開メソッド）
   * @param me ユーザー情報
   */
  public handleCastInreviewNavigation(me: Me): void {
    this.navigateBasedOnCastInreviewStatus(me);
  }

  /**
   * Appleログインが有効かどうかをチェック
   */
  isAppleLoginEnabled(): boolean {
    return (
      !this.platform.is("android") && AppConfig.META_DATA.appID === "member"
    );
  }

  /**
   * 招待トークンを取得
   */
  getInviteToken(): string {
    const url = localStorage.getItem(LocalStorageKey.User.DeepLinks);
    const token: string = Util().getQueryVariable(url, "it");
    if (token) {
      localStorage.setItem(LocalStorageKey.User.CkInviteToken, token);
      return token;
    } else {
      const it: string = localStorage.getItem(
        LocalStorageKey.User.CkInviteToken
      );
      return it || "";
    }
  }

  /**
   * WebTrackトークンを取得
   */
  getWebTrackToken(): string {
    const url = localStorage.getItem(LocalStorageKey.User.DeepLinks);
    if (url === null) {
      return "";
    }
    const token: string = Util().getQueryVariable(url, "t");
    if (token) {
      localStorage.setItem(LocalStorageKey.User.CkWebTrack, token);
      return token;
    } else {
      const webTrackToken = localStorage.getItem(
        LocalStorageKey.User.CkWebTrack
      );
      if (webTrackToken === null) {
        return "";
      }
      return webTrackToken;
    }
  }

  // Private methods

  private async authPhoneNumber(
    signUpData: SignUpDataOAuth = null,
    category: "with_line" | null = null,
    forCast: boolean = false
  ): Promise<void> {
    this.setCreateSignupData(forCast);
    this.navParams.set({ authType: forCast ? "signin_for_cast" : "signin" });
    this.navCtrl.navigateForward("RequestPhoneAuthPage");

    this.events.unsubscribe("try:signin:phone-auth");
    this.events.subscribe(
      "try:signin:phone-auth",
      (data: SignUpDataPhoneAuth) => {
        if (category === "with_line") {
          signUpData.setSignUpDataPhoneAuth(data);
          this.tryLoginForLineWithPhone(signUpData);
        } else {
          this.trySigninWithPhoneAuth(data, forCast);
        }
      }
    );
  }

  private async authLine(): Promise<void> {
    localStorage.removeItem(LocalStorageKey.User.LINELoginType);
    this.lineProvider.startAuthLine();
  }

  private async authLineWithPatoPassport(): Promise<void> {
    localStorage.removeItem(LocalStorageKey.User.LINELoginType);
    this.lineProvider.startAuthLineWithPatoPassport();
  }

  private async authLineWithPatoPassportCordova(): Promise<void> {
    try {
      const accessToken =
        await this.lineProvider.startAuthLineWithPatoPassportCordova();
      if (accessToken) {
        const loading = await this.loadingCtrl.create();
        await loading.present();

        try {
          const data: Me = await firstValueFrom(
            this.auth.signInWithLineAccessToken(accessToken, true)
          );
          this.tryLoginToken(data);
        } catch (error) {
          if (error?.error) {
            this.showGuestAccountErrorAlert();
          } else {
            this.alertCtrl.basicAlert("エラー", "LINEログインに失敗しました");
          }
        } finally {
          await loading.dismiss();
        }
      }
    } catch (error) {
      console.error("LINE Cordova login error:", error);
    }
  }

  private async authFacebook(): Promise<void> {
    if (
      (this.platform.is("android") && !AppConfig.IS_BROWSER) ||
      AppConfig.META_DATA.appID === "member"
    ) {
      await this.tryFacebookLoginForSDK();
    } else {
      this.facebookProvider.authFacebook();
    }
  }

  private async authMail(): Promise<void> {
    this.setCreateSignupData(false);
    this.navParams.set({ authType: "signin" });
    this.navCtrl.navigateForward("RequestMailAuthPage", { animated: false });

    this.events.unsubscribe("try:signin:phone-auth");
    this.events.subscribe(
      "try:signin:phone-auth",
      (data: SignUpDataPhoneAuth) => {
        this.trySigninWithPhoneAuth(data);
      }
    );
  }

  private async authApple(forCast: boolean): Promise<void> {
    try {
      const state = Util().random();
      const nonce = Util().random();
      const signInWithAppleResponse = await appleAuth(state, nonce);

      const data: Me = await firstValueFrom(
        this.auth.signUpWithApple({
          apple_id: signInWithAppleResponse.response.user,
          identity_token: signInWithAppleResponse.response.identityToken,
          given_name: signInWithAppleResponse.response.givenName,
          family_name: signInWithAppleResponse.response.familyName,
          for_cast: forCast,
          nonce: nonce,
        })
      );
      const token: string = data.token;
      delete data.token;
      this.successLogin(data, token);
    } catch (error) {
      console.log("Apple login failed", JSON.stringify(error));
      // ゲストユーザーの場合は特別なエラーメッセージを表示
      if (error?.error && forCast) {
        this.showGuestAccountErrorAlert();
      } else {
        this.alertCtrl.basicAlert("エラー", "Appleログインに失敗しました");
      }
    }
  }

  private async tryFacebookLoginForSDK(): Promise<void> {
    try {
      const res: FacebookLoginResponse = await this.facebookProvider.login();
      if (res.status !== "connected") {
        throw new FacebookLoginError();
      }

      await this.loadingStart();
      const data: Me = await this.auth
        .signInWithFacebook(res.authResponse.userID)
        .toPromise();

      if (data.isNeedRegister()) {
        return this.facebookSignUp(res);
      }

      const token: string = data.token;
      delete data.token;
      this.successLogin(data, token);
    } catch (e) {
      this.alertCtrl.basicAlert("エラー", "Facebookログインに失敗しました");
    }
  }

  private setCreateSignupData(forCast: boolean = false): SignUpData {
    const signupData = new SignUpData({
      invite_token: this.getInviteToken(),
      web_track_token: this.getWebTrackToken(),
      for_cast: forCast,
    });
    localStorage.setItem(
      LocalStorageKey.Guest.SignupData,
      JSON.stringify(signupData)
    );
    return signupData;
  }

  private trackLogin(eventTag: string): void {
    this.tracker.trackForFirebaseAnalytics(eventTag);
  }

  private getProviderName(provider: LoginProvider): string {
    const names = {
      phone: "電話番号",
      phone_for_cast: "電話番号",
      line: "LINE",
      facebook: "Facebook",
      email: "メールアドレス",
      apple: "Apple",
      apple_for_cast: "Apple",
      line_with_pato_passport: "LINE",
    };
    return names[provider] || provider;
  }

  private async loadingStart(): Promise<void> {
    if (!this.loading) {
      this.loading = await this.loadingCtrl.create();
      await this.loading.present();
    }
  }

  private loadingDismiss(): void {
    if (this.loading) {
      this.loading.dismiss();
      this.loading = null;
    }
  }

  // 以下は既存のメソッドをコピー（省略していますが、実際には必要なメソッドを移植します）

  loginWithTokenButton(tokenForDev: string): void {
    this.auth.setToken(tokenForDev).then(async () => {
      // UUIDも設定
      await this.auth.ensureUuid();

      this.auth
        .authenticate()
        .then((res) => {
          if (res) {
            this.navCtrl.navigateRoot("TabsPage");
          } else {
            this.auth.removeToken();
            this.auth.removeUuid();
            this.alertCtrl.basicAlert("エラー", "認証エラーです");
          }
        })
        .catch((error) => {
          this.auth.removeToken();
          this.auth.removeUuid();
          this.alertCtrl.basicAlert("エラー", error.message);
        });
    });
  }

  /**
   * 電話番号認証後のサインイン処理
   */
  private async trySigninWithPhoneAuth(
    data: SignUpDataPhoneAuth,
    forCast: boolean = false
  ) {
    this.loadingStart();
    const tmpSignupData = JSON.parse(
      localStorage.getItem(LocalStorageKey.Guest.SignupData)
    );
    const signupData = new SignUpData({
      code: data.code,
      token: data.token,
      user: {
        email: data.mail,
      },
      invite_token: this.getInviteToken(),
      for_cast: forCast,
    });
    // ローカルストレージに保存していたものを復活
    signupData.setSignupData(tmpSignupData);

    try {
      // 電話番号認証
      const me: Me = await firstValueFrom(
        this.auth.signinPhoneAuthCode(signupData)
      );
      this.events.unsubscribe("try:signin:phone-auth");
      this.successSignupForPhoneAuth(me);
    } catch (error) {
      this.events.publish("try:signin:phone-auth:failed");

      // ゲストユーザーの場合は特別なエラーメッセージを表示
      if (error?.error && forCast) {
        this.showGuestAccountErrorAlert();
      } else {
        this.alertCtrl.basicAlert("エラー", "電話番号の認証に失敗しました");
      }
    } finally {
      this.loadingDismiss();
    }
  }

  /**
   * 電話番号認証成功時の処理
   */
  private successSignupForPhoneAuth(meData: Me): void {
    this.trackerSuccessAuth(meData);
    const token: string = meData.getToken();
    meData.deleteToken();
    const me: Me = new Me(meData);
    me.setTrueIsFirstLaunch();
    this.auth.loginWithToken(me, token).then(() => {
      this.loadingDismiss();
      if (me.isInreview() && me.isCast()) {
        if (Util().isLiffClient() || Util().isPatoForCast()) {
          this.navigateBasedOnCastInreviewStatus(me);
        } else {
          this.checkLineInreviewStatus();
        }
      } else {
        if (me.getLocationId()) {
          // アカウント作成済みなのでtabsページに遷移
          this.setRootPage(me);
        } else {
          // 電話番号認証完了画面に遷移
          this.navCtrl.navigateForward("SuccessPhoneAuthPage");
        }
      }
    });
  }

  /**
   * ログイン成功時の共通処理
   */
  private successLogin(me: Me, token: string): void {
    this.auth.loginWithToken(me, token).then(() => {
      this.trackerSuccessAuth(me);
      this.loadingDismiss();
      this.tryLinkedLineLogin();
      // キャスト応募用LINEの場合、キャスト向けナビゲーション処理を実行
      if (me.isInreview() && me.isCast()) {
        this.tracker.updateWebTrackToken();
        this.navigateBasedOnCastInreviewStatus(me);
      } else {
        // TabsPageに移動
        this.navCtrl.navigateRoot("TabsPage");
      }
      Util().deleteQueryVariable("code");
    });
  }

  /**
   * キャストのinreviewステータスに基づいてページナビゲーションを行う
   * app.component.tsのloginPatoPassportLiffメソッドと同じロジック
   * @param me ユーザー情報
   */
  private navigateBasedOnCastInreviewStatus(me: Me): void {
    if (!(Util().isLiffClient() || Util().isPatoForCast())) {
      // LIFF環境でもpato for Cast環境でもない場合は、通常ルート設定ページで遷移
      this.setRootPage(me);
      return;
    }
    switch (me.inreview_status) {
      case "interview_pass": {
        // 成功時は適切なルートページに遷移（ユーザータイプに応じて分岐）
        if (me.isInreview()) {
          this.navCtrl.navigateRoot("CastAppliedReserveScheduleSuccessPage");
        } else {
          this.setRootPage(me);
        }
        break;
      }
      case "interview_and_date_fixed":
      case "interview_and_expire_date":
      case "interview": {
        // LINE連携チェック: 連携していない場合は、LINE連携ページに遷移
        if (!me.isLinkedPatoPassportLine() && Util().isPatoForCast()) {
          this.navCtrl.navigateRoot("CastAppliedLineIntegrationPage");
        } else if (
          me.userFeatureFlag?.isEnabledNewCastAppliedCalendar &&
          [
            LocationIds.TOKYO,
            LocationIds.OSAKA,
            LocationIds.NAGOYA,
            LocationIds.FUKUOKA,
          ].indexOf(me.getLocationId() || 0) !== -1
        ) {
          if (me.is_interview_reserved) {
            this.navCtrl.navigateRoot("CastAppliedReserveScheduleSuccessPage");
          } else {
            this.navCtrl.navigateRoot("CastAppliedReserveSchedulePage");
          }
        } else {
          this.navCtrl.navigateRoot("CastAppliedPhotoUploadCompletePage");
        }
        break;
      }
      case "outsourcing_inreview":
      case "outsourcing_interview":
      case "inreview_first_screening":
      case "inreview": {
        // LINE連携チェック: 連携していない場合は、LINE連携ページに直接遷移
        if (!me.isLinkedPatoPassportLine() && Util().isPatoForCast()) {
          this.navCtrl.navigateRoot("CastAppliedLineIntegrationPage");
        } else {
          this.navCtrl.navigateRoot("CastAppliedPhotoUploadCompletePage");
        }
        break;
      }
      case "interview_reject":
      case "outsourcing_inreview_reject":
      case "inreview_reject": {
        // NOTE: 審査落ちの場合も再審査可能なようにする
        this.navCtrl.navigateRoot("CastAppliedPhotoUploadPage");
        break;
      }
      default: {
        // pato for Cast環境でLINE未連携の場合はLINE連携ページに遷移
        if (!me.isLinkedPatoPassportLine() && Util().isPatoForCast()) {
          this.navCtrl.navigateRoot("CastAppliedLineIntegrationPage");
        } else {
          // LIFF環境またはpato for Cast環境の場合は写真送付ページに遷移
          this.navCtrl.navigateRoot("CastAppliedPhotoUploadPage");
        }
        break;
      }
    }
  }

  /**
   * LINE+電話番号ログイン処理
   */
  private tryLoginForLineWithPhone(signUpData: SignUpDataOAuth): void {
    this.loadingStart();
    this.auth.requestNeedParamsForLineLoginWithPhone(signUpData).subscribe(
      (data: Me) => {
        this.loadingDismiss();
        this.tryLoginToken(data);
      },
      (error) => {
        this.loadingDismiss();
        // ゲストユーザーの場合は特別なエラーメッセージを表示
        if (error?.error && signUpData.line_login_type === "cast_applied") {
          this.showGuestAccountErrorAlert();
        } else {
          this.alertCtrl.basicAlert("エラー", "LINEログインに失敗しました");
        }
      }
    );
  }

  /**
   * Facebookサインアップ処理
   */
  private async facebookSignUp(
    facebookLoginResponse: FacebookLoginResponse
  ): Promise<void> {
    const signupData = new SignUpData({
      facebook: {
        facebook_user_id:
          AppConfig.META_DATA.facebookLoginPrefix +
          facebookLoginResponse.authResponse.userID,
        access_token: facebookLoginResponse.authResponse.accessToken,
        expires_in: facebookLoginResponse.authResponse.expiresIn,
      },
      invite_token: this.getInviteToken(),
    });

    /** FacebookAPIよりユーザーデータの取得 */
    const facebookProfile = await this.facebookProvider.getProfile(
      facebookLoginResponse.authResponse.accessToken
    );
    facebookProfile.setUserId(facebookLoginResponse.authResponse.userID);

    this.auth.signUpWithFacebook(signupData).subscribe(
      (data: Me) => {
        const token: string = data.token;
        delete data.token;
        this.successLogin(data, token);
      },
      (error) => {
        this.alertCtrl.basicAlert("エラー", "アカウントの作成に失敗しました");
      }
    );
  }

  /**
   * トークンログイン処理
   */
  private tryLoginToken(data: Me): void {
    const token: string = data.getToken();
    data.deleteToken();
    const me: Me = new Me(data);
    me.setTrueIsFirstLaunch();
    this.successLogin(me, token);
  }

  /**
   * ルートページ設定
   */
  setRootPage(me: Me): void {
    // タブレットユーザーの場合のみ専用ページに遷移する
    if (me.isGuest() && me.isTabletUserType()) {
      this.navCtrl.navigateRoot("TabletHomePage");
    } else if (me.isGuest() && me.isShopOwnerUserType()) {
      if (!me.hasUsedReady()) {
        // ショップオーナーの場合、利用が伴ってなければ、ReadyUsedPageを表示する
        this.navParams.set({
          firstLaunch: true,
        });
        this.navCtrl.navigateRoot("ReadyUsedPage");
        return;
      }
      const url = localStorage.getItem(LocalStorageKey.User.DeepLinks);
      // urlのパラメータによって画面遷移
      if (url.indexOf("CastRequestByShopOwnerPage") !== -1) {
        this.navCtrl.navigateRoot("CastRequestByShopOwnerPage");
      } else if (url.indexOf("TodayEntryCastListPage") !== -1) {
        this.navCtrl.navigateRoot("TodayEntryCastListPage");
      } else {
        this.navCtrl.navigateRoot("CastRequestListPage");
      }
    } else if (me.isGuest() && me.isSalonDisplayOnlyUserType()) {
      this.navCtrl.navigateRoot("SearchTabPage");
    } else if (me.isInreview() && me.isCast()) {
      // pato for Cast環境またはLIFF環境の場合のみ
      // inreviewステータスに基づいて適切なページに遷移
      if (Util().isLiffClient() || Util().isPatoForCast()) {
        this.navigateBasedOnCastInreviewStatus(me);
      } else {
        // Web環境などの場合は従来通りLINE友達追加ページに遷移
        this.navCtrl.navigateRoot("CastLineAppliedPage");
      }
    } else {
      // NOTE: iOS版patoの場合、利用準備が整ってなければ利用準備画面を表示する
      if (
        !me.hasUsedReady() &&
        me.isGuest() &&
        this.platform.is("ios") &&
        this.platform.is("capacitor") &&
        AppConfig.META_DATA.appID === "pato" &&
        !AppConfig.IS_BROWSER
      ) {
        this.navParams.set({
          firstLaunch: true,
        });
        this.navCtrl.navigateRoot("ReadyUsedPage");
      } else if (me.getLocationId()) {
        // アカウント作成済みなのでtabsページに遷移
        this.navCtrl.navigateRoot("TabsPage");
      } else {
        this.navCtrl.navigateForward("SuccessPhoneAuthPage");
      }
    }
  }

  /**
   * 分析用のtracker送付処理
   */
  private trackerSuccessAuth(me: Me): void {
    if (me.isNewSignupUser()) {
      this.tracker.trackForFirebaseAnalytics(FAEventTag.Load.SuccessSignUp);
      // TODO: ASP処理とIntimate処理は元のファイルから移植が必要
    } else {
      this.tracker.trackForFirebaseAnalytics(FAEventTag.Load.SuccessLogin);
    }
  }

  /**
   * LINE関連のログイン処理
   */
  async tryLinkedLineLogin(): Promise<void> {
    if (Util().isLiffClient() && this.lineProvider.isPatoVIPCastLineApp()) {
      try {
        const lineProfile =
          await this.lineProvider.initializeForLiffWithProfile();
        this.userProvider.lineLinkedLineBotUser(
          lineProfile.userId,
          this.lineProvider.getLiffId()
        );
      } catch (e) {
        console.log(e);
      }
    }
  }

  /**
   * キャストのLINE審査状況チェック
   */
  private checkLineInreviewStatus(): void {
    this.navCtrl.navigateRoot("CastLineAppliedPage");
  }

  /**
   * forCast用のゲストアカウントエラーアラート表示
   */
  showGuestAccountErrorAlert(): void {
    this.alertCtrl.create({
      header: "このアカウントではログインできません",
      message:
        "こちらのアカウントはゲスト専用のため、ログインできません。Web版patoからログインをお願いします。",
      buttons: [
        {
          text: "キャンセル",
          role: "cancel",
        },
        {
          text: "Web版patoへ",
          handler: () => {
            // Web版patoのキャスト登録ページへ誘導
            window.open("https://pato.today/", "_system");
          },
        },
      ],
    });
  }
}
